import React from 'react'
import { useFormik } from 'formik'
import PageWrapper from '../../components/PageWrapper'
import { getLocal } from '../../utils/local'
import EstateCard from '../../components/Estate/EstateCard'
import StaffService from '../../api/staff-service'
import { IStaffEstate } from '../../interfaces/staff'
import Modal from '../../components/Modal'
import FormInput from '../../components/Input'
import DefaultButton from '../../components/Button/Button'
import { createEstateSchema } from '../../validations/createEstate'
import EstateService from '../../api/estate-service'
import { showToast } from '../../utils/toast'
import Darklogo from '../../assets/icons/dark-logo.svg'
import ModalClose from '../../components/Modal/ModalClose'
import AddButton from '../../components/IconButton'
import addIcon from '../../assets/icons/Addimg.svg'
import { EstateSkeletonLoader } from '../../components/Estate/EstateSkeletonLoader'
import Metadata from '../../components/Metadata'

const Estates = () => {
  const [estates, setEstate] = React.useState<IStaffEstate[]>([])
  const [openModal, setOpenModal] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEsateCreating, setIsEstateCreating] = React.useState(false);

  const user = getLocal('user')

const isEmailVerified = user?.isEmailVerified === true

  const formik = useFormik({
    initialValues: {
      name: '',
      address: '',
    },
    validationSchema: createEstateSchema,
    validateOnMount: true,
    onSubmit: handleCreateEstate,
  })

  const handleModalClose = () => {
    setOpenModal(false)
  }

  const handleModalOpen = () => {
    setOpenModal(true)
  }

 const handleGetStaffEstates = () => {
  if (!isEmailVerified) {
    return
  }

  setIsLoading(true)

  StaffService.getStaffEstates()
    .then((data) => {
      setEstate(data)
    })
    .finally(() => {
      setIsLoading(false)
    })
}

 React.useEffect(() => {
  if (!isEmailVerified) {
    return
  }

  handleGetStaffEstates()
}, [isEmailVerified])

  function handleCreateEstate() {
    setIsEstateCreating(true);
    EstateService.createStaff(formik.values)
      .then((dat) => {
        showToast({
          description: dat.message,
          status: 'success',
          id: 'create-estate-success',
          title: 'Estate created',
        })
        StaffService.getStaffEstates().then((data) => {
          setEstate(data)
        })
      })
      .finally(() => {
        setIsEstateCreating(false);
        handleModalClose()
      })
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const estatesMetadata = {
    title: 'Estates - Safelto',
    description: 'Manage and view all estates you belong to. Create new estates and manage estate information.',
    keywords: ['estates', 'estate management', 'create estate', 'property management', 'residential estates'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/estates',
    type: 'website' as 'website'
  }

  return (
    <PageWrapper title="My Estates" description="Here are the Estates you belong to.">
      <Metadata {...estatesMetadata} />
      <div className="py-5 ">
        <AddButton
          handleModalOpen={handleModalOpen}
          buttonStyle="w-[222px]"
          icon={<img src={addIcon} alt="Add icon" />}
        >
          <div className="flex flex-col justify-center gap-1">
            <p className="text-base font-medium">Add estate</p>
            <p className="text-xs font-normal text-gray-600">Add new estate</p>
          </div>
        </AddButton>
      </div>
      <div className="flex flex-wrap w-full items-stretch gap-10 justify-start">

      {isLoading ? (
        Array(estates.length || 5) 
      .fill(null)
      .map((_, index) => <EstateSkeletonLoader key={index} />)
  ) : (
    estates.map((estate) => (
      <EstateCard
        key={estate.id}
        name={estate.estate.name}
        isVerified={estate.estate.isVerified}
        isOwner={estate.isOwner}
        id={estate.estate.id}
        accepted={estate.accepted}
        isActive={estate.isActive}
        handleGetStaffEstates={handleGetStaffEstates}
      />
    ))
  )}

      </div>
      <Modal
        open={openModal}
        close={handleModalClose}
        widthClass="w-[calc(100%-24px)] sm:max-w-md"
        modalClassName="h-auto !p-4 sm:!p-6"
      >
        <ModalClose onClick={handleCloseModal} className="absolute right-4 top-4 m-0" />
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="mb-8 flex flex-col items-center sm:mb-10">
            <a href="/" aria-label="Go to home">
              <img src={Darklogo} alt="" className="mb-8 h-10 w-10 sm:mb-10" />
            </a>
            <h1 className="text-lg font-bold">Your estate details</h1>
            <p className="text-sm text-gray-500 text-center">
              Please enter your estate name and address
            </p>
          </div>
          <FormInput
            className="mb-6 sm:mb-8"
            value={formik.values.name}
            onChange={formik.handleChange}
            name="name"
            error={formik.touched.name ? formik.errors.name : undefined}
            type="text"
            placeholder="Enter Estate name"
            size="md"
          />
          <FormInput
            className="mb-6 w-full sm:mb-8"
            value={formik.values.address}
            onChange={formik.handleChange}
            name="address"
            error={formik.touched.address ? formik.errors.address : undefined}
            type="text"
            placeholder="Enter Estate Address"
            size="md"
          />

          <DefaultButton
            type="submit"
            loading={isEsateCreating}
            className="mb-2"
            w="full"
            background="gray.900"
            color="white"
            size="md"
            style={{
              backgroundColor: !(formik.dirty && formik.isValid) ? '#DEE2E6' : 'black',
            }}
            loadingText="Creating estate"
            disabled={!(formik.dirty && formik.isValid)}
          >
            Create Estate
          </DefaultButton>
        </form>
      </Modal>
    </PageWrapper>
  )
}

export default Estates
